import dao from '@/scripts/helper/dao';

/**
 * 访问控制规则（Access Rules）
 *
 * 与 cosbrowser-server `routes/api/access-rules.js` + `middlewares/access-rules.js`
 * 配套使用，由 Rainbow `serverSetting.accessRules` 下发规则：
 *   [
 *     { match: '*',                    block: ['ak'] },
 *     { match: { appId: '1300000000' }, block: ['ak', 'oauth'] }
 *   ]
 *
 * 前端职责：
 *  1. 登录拿到 AppId 后，主动查 `/api/access-rules/me?appId=xxx`，
 *     若当前登录方式（ak/oauth）命中 blockedAuth → 立即登出 + 提示
 *  2. 业务请求被中间件拒绝（errCode=ACCESS_BLOCKED / LONGTERM_KEY_DISABLED）时，
 *     由 dao.js 的 axios 拦截器统一捕获 → 兜底处理（仅作第二道防线）
 *
 * 容错策略：
 *  - me 接口异常 / 超时 / 返回 degraded：放行，不误伤合法用户
 *    （真正的兜底拦截在接入层中间件做，前端只做体验优化）
 */

// keyBox.getKeyType() → server 端 authMethod 的映射
//   SecretKey      → ak       （永久密钥登录）
//   QcloudAccount  → oauth    （腾讯云账号登录）
const KEY_TYPE_TO_AUTH = {
  SecretKey: 'ak',
  QcloudAccount: 'oauth',
};

// 单次会话内缓存 appId → blockedAuth[]，避免重复请求
const cache = new Map();

/**
 * 把前端的 keyType 翻译成 server 的 authMethod
 * @param {string} keyType
 * @returns {'ak' | 'oauth' | ''}
 */
export function keyTypeToAuth(keyType) {
  return KEY_TYPE_TO_AUTH[keyType] || '';
}

// 匿名（未登录）查询用的缓存 key
const GLOBAL_CACHE_KEY = '__global__';

/**
 * 查询当前 appId 在 access-rules 体系下被禁的认证方式集合
 *
 * @param {string} appId
 * @returns {Promise<string[]>} blockedAuth 数组（如 ['ak'] / ['ak','oauth'] / []），
 *                              失败一律返回 []（放行兜底）
 */
export async function fetchBlockedAuth(appId) {
  const id = appId ? String(appId) : '';
  if (!/^\d{6,15}$/.test(id)) return [];

  if (cache.has(id)) return cache.get(id);

  try {
    // dao.js 的 axios 拦截器对 code === 0 会剥到 data，对 code !== 0 会 reject
    // 这里 me 接口成功响应是 { code: 0, data: { blockedAuth, appId } }，
    // 拦截器返回 data 部分（即 { blockedAuth, appId }）
    const data = await dao.getAccessRulesMe(id);
    const blockedAuth = Array.isArray(data && data.blockedAuth) ? data.blockedAuth : [];
    cache.set(id, blockedAuth);
    return blockedAuth;
  } catch (e) {
    // 接口异常 / Rainbow 抖动 / 网络问题 一律放行
    return [];
  }
}

/**
 * 查询"全局通配规则"（match:'*'）下被禁的认证方式集合。
 *
 * 用途：登录页等未登录场景，要判断"AK/SK 登录方式是不是被全局停用了"，
 *      据此决定 UI 上隐藏 AK 表单 / 历史会话入口等。
 *
 * 实现：调 /api/access-rules/me 时不传 appId，后端只评估 match:'*' 通配规则。
 *
 * 容错：接口异常 / 网络抖动一律返回 []，绝不在登录页误隐藏入口。
 *
 * @returns {Promise<string[]>}
 */
export async function fetchGlobalBlockedAuth() {
  if (cache.has(GLOBAL_CACHE_KEY)) return cache.get(GLOBAL_CACHE_KEY);

  try {
    // 不传 appId（dao.getAccessRulesMe 接收 undefined 时，params: { appId: undefined } 不会带上该字段）
    const data = await dao.getAccessRulesMe();
    const blockedAuth = Array.isArray(data && data.blockedAuth) ? data.blockedAuth : [];
    cache.set(GLOBAL_CACHE_KEY, blockedAuth);
    return blockedAuth;
  } catch (e) {
    return [];
  }
}

/**
 * 判断当前用户当前登录方式是否被 access-rules 拦截
 *
 * @param {object} userInfo  至少包含 AppId
 * @param {string} keyType   keyBox.getKeyType() 的值
 * @returns {Promise<{ blocked: boolean, blockedAuth: string[], currentAuth: string }>}
 */
export async function checkBlocked(userInfo, keyType) {
  const appId = userInfo && userInfo.AppId ? String(userInfo.AppId) : '';
  const currentAuth = keyTypeToAuth(keyType);

  if (!appId || !currentAuth) {
    return { blocked: false, blockedAuth: [], currentAuth };
  }

  const blockedAuth = await fetchBlockedAuth(appId);
  return {
    blocked: blockedAuth.includes(currentAuth),
    blockedAuth,
    currentAuth,
  };
}

/**
 * 清缓存（登出 / 切换账号时调用）
 */
export function clearCache() {
  cache.clear();
}

export default {
  fetchBlockedAuth,
  fetchGlobalBlockedAuth,
  checkBlocked,
  keyTypeToAuth,
  clearCache,
};
